// Demand List (/demands): what brokers tell us their parties are looking for,
// every contact about it, and the parties that have gone quiet (no sale and
// no contact for a while) so we can call them or their broker again.
// Two tabs: Demands · Gone quiet. Data: demands, demandActivities (both
// live), sales (last sale + brokers per party) and entities.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { collection, deleteDoc, doc, onSnapshot, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { EntityFormModal } from '../components/EntityFormModal';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { Button, ConfirmModal, DeleteIconButton, FormField, Modal, ModalBanner, Paging } from '../components/ui';
import { EntityCombobox } from '../components/ui/EntityCombobox';
import { OptionCombobox } from '../components/ui/OptionCombobox';
import { HeaderSearch, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { saleBrokerRows } from '../utils/saleMoney';
import type { Demand, DemandActivity, DemandActivityKind, DemandStatus, Entity, SaleInvoice } from '../types';

// ── Small helpers ─────────────────────────────────────────────────────────

const SHAPES    = ['Round', 'Princess', 'Oval', 'Cushion', 'Emerald', 'Pear', 'Marquise', 'Radiant', 'Asscher', 'Heart', 'Other'];
const COLORS    = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'Fancy'];
const CLARITIES = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
/** Standard grades first, then the company's own loose grades ("G-H", "VS"…), each value once. */
function mergeGrades(standard: string[], custom?: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of [...standard, ...(custom ?? [])]) {
    const t = v.trim(); const k = t.toLowerCase();
    if (t && !seen.has(k)) { seen.add(k); out.push(t); }
  }
  return out;
}
const LABS      = ['GIA', 'IGI', 'HRD', 'AGS', 'GCAL', 'EGL', 'Loose'];

const STATUS: Record<DemandStatus, { label: string; color: string }> = {
  open:    { label: 'Open',    color: 'var(--primary)' },
  offered: { label: 'Offered', color: 'var(--warning)' },
  won:     { label: 'Deal Done', color: 'var(--success)' },
  lost:    { label: 'No Deal',   color: 'var(--danger)' },
};
const STATUS_ORDER: DemandStatus[] = ['open', 'offered', 'won', 'lost'];

const KIND: Record<DemandActivityKind, { label: string; color: string }> = {
  call:     { label: 'Call',     color: 'var(--primary)' },
  whatsapp: { label: 'WhatsApp', color: 'var(--success)' },
  meeting:  { label: 'Meeting',  color: 'var(--accent-violet)' },
  offer:    { label: 'Offer',    color: 'var(--warning)' },
  note:     { label: 'Note',     color: 'var(--text-3)' },
};
const KIND_ORDER: DemandActivityKind[] = ['call', 'whatsapp', 'meeting', 'offer', 'note'];

const QUIET_OPTIONS = [30, 60, 90, 180];

function localISO(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function daysSince(date?: string): number | null {
  if (!date) return null;
  const a = new Date(`${date}T00:00:00`).getTime();
  const b = new Date(`${localISO()}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}
const fmtDate = (s?: string) =>
  s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const maxDate = (...d: (string | undefined)[]) => d.filter(Boolean).sort().pop();
const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : undefined; };
function clean<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as T;
}
const muted = <span style={{ opacity: 0.4 }}>—</span>;

/** "Round · 1.00-1.20 · 10–20 ct" */
function stoneLine(d: Demand): string {
  const ct = d.caratFrom || d.caratTo
    ? `${d.caratFrom ?? ''}${d.caratFrom && d.caratTo ? '–' : ''}${d.caratTo ?? ''} ct`
    : '';
  return [d.shape, d.size, ct].filter(Boolean).join(' · ');
}

function StatusPill({ status }: { status: DemandStatus }) {
  const s = STATUS[status];
  return <span className="dm-pill" style={{ ['--pc' as string]: s.color }}>{s.label}</span>;
}
function KindPill({ kind }: { kind: DemandActivityKind }) {
  const k = KIND[kind];
  return <span className="dm-pill" style={{ ['--pc' as string]: k.color }}>{k.label}</span>;
}
function IconDemandEmpty() {
  return <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="3.5" width="14" height="17" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9 3.5h6v2.5H9zM8.5 10h7M8.5 13.5h7M8.5 17h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

/** Several brokers: chips with × plus a picker that adds one. */
function BrokerPicker({ brokers, value, onChange, onCreate }: {
  brokers: Entity[]; value: string[]; onChange: (ids: string[]) => void;
  /** "+ Create New Entity" at the top of the list (opens the Add Entity form). */
  onCreate?: () => void;
}) {
  const name = (id: string) => brokers.find(b => b.id === id)?.companyName ?? 'Broker';
  return (
    <div className="dm-brokers">
      {value.map(id => (
        <span key={id} className="opt-chip">
          {name(id)}
          <button type="button" className="opt-chip-x" title={`Remove ${name(id)}`} onClick={() => onChange(value.filter(x => x !== id))}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </span>
      ))}
      <div className="form-group" style={{ margin: 0, width: 220, flexShrink: 0 }}>
        <EntityCombobox
          entities={brokers.filter(b => !value.includes(b.id)).map(b => ({ id: b.id, companyName: b.companyName }))}
          value="" onChange={id => { if (id) onChange([...value, id]); }}
          placeholder={value.length ? 'Add another broker…' : 'Add a broker…'}
          createLabel={onCreate ? '+ Create New Entity' : undefined} onCreateNew={onCreate}
        />
      </div>
    </div>
  );
}

// ── Add / Edit demand, with its activity log ──────────────────────────────

interface DemandForm {
  date: string; partyId: string; brokerIds: string[];
  shape: string; size: string; caratFrom: string; caratTo: string;
  color: string; clarity: string; lab: string; pricePerCt: string;
  notes: string; status: DemandStatus; followUpDate: string;
}

function DemandModal({ editing, parties, brokers, activities, presetPartyId, onClose }: {
  editing: Demand | null;
  parties: Entity[]; brokers: Entity[];
  activities: DemandActivity[];
  presetPartyId?: string;
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const isEdit = !!editing;
  const [f, setF] = useState<DemandForm>(() => ({
    date: editing?.date ?? localISO(), partyId: editing?.partyId ?? presetPartyId ?? '', brokerIds: editing?.brokerIds ?? [],
    shape: editing?.shape ?? '', size: editing?.size ?? '',
    caratFrom: editing?.caratFrom != null ? String(editing.caratFrom) : '', caratTo: editing?.caratTo != null ? String(editing.caratTo) : '',
    color: editing?.color ?? '', clarity: editing?.clarity ?? '', lab: editing?.lab ?? '',
    pricePerCt: editing?.pricePerCt != null ? String(editing.pricePerCt) : '',
    notes: editing?.notes ?? '', status: editing?.status ?? 'open', followUpDate: editing?.followUpDate ?? '',
  }));
  const set = <K extends keyof DemandForm>(k: K, v: DemandForm[K]) => setF(x => ({ ...x, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  // "+ Create New Entity" from the Party or Broker list: the new company is picked straight away.
  const [createFor, setCreateFor] = useState<'party' | 'broker' | null>(null);
  const [created, setCreated] = useState<Entity[]>([]);
  const partyList  = useMemo(() => [...parties, ...created.filter(e => (e.types ?? []).includes('customer') && !parties.some(p => p.id === e.id))], [parties, created]);
  const brokerList = useMemo(() => [...brokers, ...created.filter(e => (e.types ?? []).includes('broker') && !brokers.some(b => b.id === e.id))], [brokers, created]);
  const partyName = partyList.find(p => p.id === f.partyId)?.companyName;
  const log = useMemo(() => activities.filter(a => editing && a.demandId === editing.id)
    .sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt)), [activities, editing]);

  async function save() {
    if (!f.partyId) { setError('Pick the party.'); return; }
    if (!f.date) { setError('Pick the date.'); return; }
    const from = num(f.caratFrom), to = num(f.caratTo);
    if (from && to && from > to) { setError('Carat from must be less than carat to.'); return; }
    const db = getClientDb();
    if (!db || !business || !user) return;
    setSaving(true); setError('');
    try {
      const ref = editing ? doc(db, 'demands', editing.id) : doc(collection(db, 'demands'));
      const data = clean({
        date: f.date, partyId: f.partyId, brokerIds: f.brokerIds,
        shape: f.shape.trim(), size: f.size.trim(), caratFrom: from, caratTo: to,
        color: f.color.trim(), clarity: f.clarity.trim(), lab: f.lab.trim(), pricePerCt: num(f.pricePerCt),
        notes: f.notes.trim(), status: f.status, followUpDate: f.followUpDate,
      });
      if (editing) {
        // Cleared boxes have to be removed, not left with their old value.
        const blanks = Object.fromEntries(['shape', 'size', 'caratFrom', 'caratTo', 'color', 'clarity', 'lab', 'pricePerCt', 'notes', 'followUpDate']
          .filter(k => !(k in data)).map(k => [k, null]));
        await updateDoc(ref, { ...data, ...blanks, updatedAt: new Date().toISOString() });
      } else {
        await setDoc(ref, {
          ...data, id: ref.id, businessId: business.id,
          createdAt: new Date().toISOString(), createdBy: user.id, createdByName: user.name ?? '',
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the demand.');
    }
    setSaving(false);
  }

  async function remove() {
    const db = getClientDb();
    if (!db || !editing) return;
    setSaving(true);
    try {
      // The demand and its own activity lines go together.
      const b = writeBatch(db);
      b.delete(doc(db, 'demands', editing.id));
      log.forEach(a => b.delete(doc(db, 'demandActivities', a.id)));
      await b.commit();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the demand.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} size="xl" hideHead
      footer={<div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        {isEdit && <Button variant="secondary" onClick={() => setConfirmDel(true)} disabled={saving} style={{ color: 'var(--danger)', marginRight: 'auto' }}>Delete</Button>}
        {!isEdit && <span style={{ marginRight: 'auto' }} />}
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>{isEdit ? 'Save' : 'Add Demand'}</Button>
      </div>}
    >
      <ModalBanner title={partyName || (isEdit ? 'Demand' : 'New Demand')} eyebrow={isEdit ? 'Demand' : 'Add Demand'}
        right={isEdit ? <StatusPill status={editing!.status} /> : undefined} onClose={onClose} />
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className={isEdit ? 'dm-edit-grid' : undefined}>
        <div>
          <div className="form-section-label">Who and when</div>
          <div className="form-row-3">
            <FormField label="Party" required>
              <EntityCombobox entities={partyList.map(p => ({ id: p.id, companyName: p.companyName }))}
                value={f.partyId} onChange={v => set('partyId', v)} placeholder="Pick the party…"
                createLabel="+ Create New Entity" onCreateNew={() => setCreateFor('party')} />
            </FormField>
            <FormField label="Date" required>
              <input type="date" value={f.date} max={localISO()} onChange={e => set('date', e.target.value)} />
            </FormField>
            <FormField label="Status">
              <select value={f.status} onChange={e => set('status', e.target.value as DemandStatus)}>
                {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Brokers">
            <BrokerPicker brokers={brokerList} value={f.brokerIds} onChange={v => set('brokerIds', v)} onCreate={() => setCreateFor('broker')} />
          </FormField>

          <div className="form-section-label" style={{ marginTop: 6 }}>What they want</div>
          <div className="form-row-4">
            <FormField label="Shape"><OptionCombobox value={f.shape} onChange={v => set('shape', v)} options={SHAPES} placeholder="Round" /></FormField>
            <FormField label="Size"><input value={f.size} onChange={e => set('size', e.target.value)} placeholder="1.00-1.20" /></FormField>
            <FormField label="Carat from"><input type="number" className="no-spinner" step="0.01" min="0" value={f.caratFrom} onChange={e => set('caratFrom', e.target.value)} placeholder="10" /></FormField>
            <FormField label="Carat to"><input type="number" className="no-spinner" step="0.01" min="0" value={f.caratTo} onChange={e => set('caratTo', e.target.value)} placeholder="20" /></FormField>
          </div>
          <div className="form-row-4">
            <FormField label="Color"><OptionCombobox value={f.color} onChange={v => set('color', v)} options={mergeGrades(COLORS, settings.looseColorOptions)} placeholder="G-H" /></FormField>
            <FormField label="Clarity"><OptionCombobox value={f.clarity} onChange={v => set('clarity', v)} options={mergeGrades(CLARITIES, settings.looseClarityOptions)} placeholder="VS1-VS2" /></FormField>
            <FormField label="Lab"><OptionCombobox value={f.lab} onChange={v => set('lab', v)} options={LABS} placeholder="GIA or Loose" /></FormField>
            <FormField label={`Price/ct (${settings.currency})`}><input type="number" className="no-spinner" step="0.01" min="0" value={f.pricePerCt} onChange={e => set('pricePerCt', e.target.value)} placeholder="What they want to pay" /></FormField>
          </div>
          <div className="form-row-2">
            <FormField label="Follow up on"><input type="date" value={f.followUpDate} onChange={e => set('followUpDate', e.target.value)} /></FormField>
            <FormField label="Notes"><input value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything else they said" /></FormField>
          </div>
        </div>

        {isEdit && (
          <div className="dm-log">
            <div className="form-section-label">Activity</div>
            <ActivityForm partyId={editing!.partyId} demandId={editing!.id} brokers={brokers.filter(b => editing!.brokerIds.includes(b.id))} compact />
            <ActivityList items={log} brokers={brokers} />
          </div>
        )}
      </div>

      <EntityFormModal open={!!createFor} onClose={() => setCreateFor(null)}
        defaultTypes={createFor === 'broker' ? ['broker'] : ['customer']}
        onSaved={ent => {
          setCreated(c => [...c, ent]);
          if (createFor === 'broker') set('brokerIds', [...f.brokerIds, ent.id]); else set('partyId', ent.id);
          setCreateFor(null);
        }} />
      {confirmDel && (
        <ConfirmModal open title="Delete this demand?"
          message="The demand and its activity lines will be removed. This can't be undone."
          confirmLabel="Delete" loading={saving} onConfirm={remove} onClose={() => setConfirmDel(false)} />
      )}
    </Modal>
  );
}

// ── Add several demands at once (Excel style grid) ───────────────────────

interface GridRow {
  key: string; partyId: string; shape: string; size: string; caratFrom: string; caratTo: string;
  color: string; clarity: string; lab: string; pricePerCt: string; followUpDate: string; notes: string;
}
let rowSeq = 0;
const newRow = (partyId = ''): GridRow => ({
  key: `r${++rowSeq}`, partyId, shape: '', size: '', caratFrom: '', caratTo: '',
  color: '', clarity: '', lab: '', pricePerCt: '', followUpDate: '', notes: '',
});
/** Anything typed beyond the party = this row is meant to be saved. */
const rowStarted = (r: GridRow) => !!(r.partyId || r.shape || r.size || r.caratFrom || r.caratTo || r.color
  || r.clarity || r.lab || r.pricePerCt || r.followUpDate || r.notes);

const GTD: CSSProperties = { padding: 0, verticalAlign: 'middle' };
const GTD_TEXT: CSSProperties = { ...GTD, padding: '6px 8px', fontSize: 11 };
const GTH: CSSProperties = {
  ...GTD_TEXT, fontSize: 9.5, fontWeight: 700, color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 2,
};
const MISSING_BG = 'color-mix(in srgb, var(--danger) 12%, transparent)';
const GRID_COLS = 11;   // keyboard nav: Party … Notes

function AddDemandsModal({ parties, brokers, presetPartyId, onClose }: {
  parties: Entity[]; brokers: Entity[]; presetPartyId?: string; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const [date, setDate] = useState(localISO());
  const [brokerIds, setBrokerIds] = useState<string[]>([]);
  const [status, setStatus] = useState<DemandStatus>('open');
  const [rows, setRows] = useState<GridRow[]>(() => [newRow(presetPartyId)]);
  const [showMissing, setShowMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createFor, setCreateFor] = useState<{ kind: 'party'; rowKey: string } | { kind: 'broker' } | null>(null);
  const [created, setCreated] = useState<Entity[]>([]);
  const partyList  = useMemo(() => [...parties, ...created.filter(e => (e.types ?? []).includes('customer') && !parties.some(p => p.id === e.id))], [parties, created]);
  const brokerList = useMemo(() => [...brokers, ...created.filter(e => (e.types ?? []).includes('broker') && !brokers.some(b => b.id === e.id))], [brokers, created]);
  const partyOpts  = partyList.map(p => ({ id: p.id, companyName: p.companyName }));
  const nav = useGridKeyNav(rows.length, GRID_COLS);
  const colorOpts   = mergeGrades(COLORS, settings.looseColorOptions);
  const clarityOpts = mergeGrades(CLARITIES, settings.looseClarityOptions);

  const upd = (key: string, k: keyof GridRow, v: string) => setRows(rs => rs.map(r => r.key === key ? { ...r, [k]: v } : r));
  const started = rows.filter(rowStarted);
  const missingParty = (r: GridRow) => rowStarted(r) && !r.partyId && (showMissing || !!(r.shape || r.size || r.caratFrom || r.color || r.clarity || r.pricePerCt));
  const toFix = rows.filter(r => rowStarted(r) && !r.partyId).length;

  async function save() {
    if (started.length === 0) { setError('Fill in at least one row.'); return; }
    if (toFix > 0) { setShowMissing(true); setError(`Pick the party on ${toFix} row${toFix === 1 ? '' : 's'}.`); return; }
    const bad = started.find(r => num(r.caratFrom) && num(r.caratTo) && num(r.caratFrom)! > num(r.caratTo)!);
    if (bad) { setError('On a row, Carat from is more than Carat to.'); return; }
    const db = getClientDb();
    if (!db || !business || !user) return;
    setSaving(true); setError('');
    try {
      const b = writeBatch(db);
      const now = new Date().toISOString();
      for (const r of started) {
        const ref = doc(collection(db, 'demands'));
        b.set(ref, clean({
          id: ref.id, businessId: business.id, date, partyId: r.partyId, brokerIds, status,
          shape: r.shape.trim(), size: r.size.trim(), caratFrom: num(r.caratFrom), caratTo: num(r.caratTo),
          color: r.color.trim(), clarity: r.clarity.trim(), lab: r.lab.trim(), pricePerCt: num(r.pricePerCt),
          followUpDate: r.followUpDate, notes: r.notes.trim(),
          createdAt: now, createdBy: user.id, createdByName: user.name ?? '',
        }));
      }
      await b.commit();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the demands.');
    }
    setSaving(false);
  }

  const n = started.length;
  return (
    <Modal open onClose={onClose} size="full" hideHead
      footer={<div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        {toFix > 0 && showMissing && <span className="dm-fix">{toFix} to fix</span>}
        <span style={{ marginRight: 'auto', fontSize: 12.5, color: 'var(--text-3)' }}>{n} demand{n === 1 ? '' : 's'} ready</span>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>{n > 1 ? `Add ${n} Demands` : 'Add Demand'}</Button>
      </div>}
    >
      <ModalBanner title="New Demands" eyebrow="Add Demands" onClose={onClose} />
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-section-label">For every row</div>
      <div className="dm-grid-head">
        <div style={{ width: 150 }}>
          <FormField label="Date"><input type="date" value={date} max={localISO()} onChange={e => setDate(e.target.value)} /></FormField>
        </div>
        <div style={{ width: 140 }}>
          <FormField label="Status">
            <select value={status} onChange={e => setStatus(e.target.value as DemandStatus)}>
              {STATUS_ORDER.map(st => <option key={st} value={st}>{STATUS[st].label}</option>)}
            </select>
          </FormField>
        </div>
        <div style={{ flex: '0 1 auto', minWidth: 220 }}>
          <FormField label="Brokers">
            <BrokerPicker brokers={brokerList} value={brokerIds} onChange={setBrokerIds} onCreate={() => setCreateFor({ kind: 'broker' })} />
          </FormField>
        </div>
      </div>

      <div className="form-section-label" style={{ marginTop: 4 }}>Demands (one per row)</div>
      <div className="dm-grid-wrap">
        <table className="excel-grid-table">
          <thead><tr>
            <th style={{ ...GTH, width: 30, textAlign: 'center' }}>#</th>
            <th style={{ ...GTH, minWidth: 200 }}>Party *</th>
            <th style={{ ...GTH, minWidth: 100 }}>Shape</th>
            <th style={{ ...GTH, minWidth: 90 }}>Size</th>
            <th style={{ ...GTH, width: 70 }}>Ct from</th>
            <th style={{ ...GTH, width: 70 }}>Ct to</th>
            <th style={{ ...GTH, minWidth: 80 }}>Color</th>
            <th style={{ ...GTH, minWidth: 90 }}>Clarity</th>
            <th style={{ ...GTH, minWidth: 80 }}>Lab</th>
            <th style={{ ...GTH, width: 100 }}>Price/ct</th>
            <th style={{ ...GTH, width: 130 }}>Follow up</th>
            <th style={{ ...GTH, minWidth: 180 }}>Notes</th>
            <th className="excel-grid-remove-col" style={{ ...GTH, width: 34 }} />
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td style={{ ...GTD_TEXT, textAlign: 'center', color: 'var(--text-3)' }}>{i + 1}</td>
                <td style={{ ...GTD, background: missingParty(r) ? MISSING_BG : undefined }}>
                  <EntityCombobox entities={partyOpts} value={r.partyId} onChange={v => upd(r.key, 'partyId', v)}
                    placeholder="Party…" inputClassName="grid-cell"
                    cellRef={nav.cellRef(i, 0)} onKeyDown={nav.onCellKeyDown(i, 0)}
                    createLabel="+ Create New Entity" onCreateNew={() => setCreateFor({ kind: 'party', rowKey: r.key })} />
                </td>
                <td style={GTD}><OptionCombobox className="grid-cell" value={r.shape} onChange={v => upd(r.key, 'shape', v)} options={SHAPES}
                  cellRef={nav.cellRef(i, 1)} onKeyDown={nav.onCellKeyDown(i, 1)} /></td>
                <td style={GTD}><input className="grid-cell" value={r.size} onChange={e => upd(r.key, 'size', e.target.value)}
                  ref={nav.cellRef(i, 2)} onKeyDown={nav.onCellKeyDown(i, 2)} /></td>
                <td style={GTD}><input className="grid-cell no-spinner" type="number" step="0.01" min="0" value={r.caratFrom} onChange={e => upd(r.key, 'caratFrom', e.target.value)}
                  ref={nav.cellRef(i, 3)} onKeyDown={nav.onCellKeyDown(i, 3)} style={{ textAlign: 'right' }} /></td>
                <td style={GTD}><input className="grid-cell no-spinner" type="number" step="0.01" min="0" value={r.caratTo} onChange={e => upd(r.key, 'caratTo', e.target.value)}
                  ref={nav.cellRef(i, 4)} onKeyDown={nav.onCellKeyDown(i, 4)} style={{ textAlign: 'right' }} /></td>
                <td style={GTD}><OptionCombobox className="grid-cell" value={r.color} onChange={v => upd(r.key, 'color', v)} options={colorOpts}
                  cellRef={nav.cellRef(i, 5)} onKeyDown={nav.onCellKeyDown(i, 5)} /></td>
                <td style={GTD}><OptionCombobox className="grid-cell" value={r.clarity} onChange={v => upd(r.key, 'clarity', v)} options={clarityOpts}
                  cellRef={nav.cellRef(i, 6)} onKeyDown={nav.onCellKeyDown(i, 6)} /></td>
                <td style={GTD}><OptionCombobox className="grid-cell" value={r.lab} onChange={v => upd(r.key, 'lab', v)} options={LABS}
                  cellRef={nav.cellRef(i, 7)} onKeyDown={nav.onCellKeyDown(i, 7)} /></td>
                <td style={GTD}><input className="grid-cell no-spinner" type="number" step="0.01" min="0" value={r.pricePerCt} onChange={e => upd(r.key, 'pricePerCt', e.target.value)}
                  ref={nav.cellRef(i, 8)} onKeyDown={nav.onCellKeyDown(i, 8)} style={{ textAlign: 'right' }} /></td>
                <td style={GTD}><input className="grid-cell" type="date" value={r.followUpDate} onChange={e => upd(r.key, 'followUpDate', e.target.value)}
                  ref={nav.cellRef(i, 9)} onKeyDown={nav.onCellKeyDown(i, 9)} /></td>
                <td style={GTD}><input className="grid-cell" value={r.notes} onChange={e => upd(r.key, 'notes', e.target.value)}
                  ref={nav.cellRef(i, 10)} onKeyDown={nav.onCellKeyDown(i, 10)} /></td>
                <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                  <DeleteIconButton onClick={() => setRows(rs => rs.length > 1 ? rs.filter(x => x.key !== r.key) : [newRow()])} />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={13} style={{ padding: 0, background: 'var(--primary-light)' }}>
                <button type="button" className="dm-add-row" onClick={() => {
                  const last = rows[rows.length - 1];
                  // A new row starts with the party of the row above, to add several stones for one party quickly.
                  setRows(rs => [...rs, newRow(last?.partyId ?? '')]);
                }}>
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                  Add Row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <EntityFormModal open={!!createFor} onClose={() => setCreateFor(null)}
        defaultTypes={createFor?.kind === 'broker' ? ['broker'] : ['customer']}
        onSaved={ent => {
          setCreated(c => [...c, ent]);
          if (createFor?.kind === 'broker') setBrokerIds(ids => [...ids, ent.id]);
          else if (createFor?.kind === 'party') upd(createFor.rowKey, 'partyId', ent.id);
          setCreateFor(null);
        }} />
    </Modal>
  );
}

// ── Activity: add one / list them ─────────────────────────────────────────

function ActivityForm({ partyId, demandId, brokers, compact, onSaved }: {
  partyId: string; demandId?: string; brokers: Entity[]; compact?: boolean; onSaved?: () => void;
}) {
  const { user, business } = useAuth();
  const [kind, setKind] = useState<DemandActivityKind>('call');
  const [date, setDate] = useState(localISO());
  const [brokerId, setBrokerId] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function add() {
    if (!text.trim()) { setError('Write what was said.'); return; }
    const db = getClientDb();
    if (!db || !business || !user) return;
    setSaving(true); setError('');
    try {
      const ref = doc(collection(db, 'demandActivities'));
      await setDoc(ref, clean({
        id: ref.id, businessId: business.id, partyId, demandId, brokerId: brokerId || undefined,
        date, kind, text: text.trim(),
        createdAt: new Date().toISOString(), createdBy: user.id, createdByName: user.name ?? '',
      }));
      // Keep the demand's "Last contact" in step for the list.
      if (demandId) await updateDoc(doc(db, 'demands', demandId), { lastContactDate: date }).catch(() => {});
      setText('');
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    }
    setSaving(false);
  }

  return (
    <div className={`dm-actform${compact ? ' compact' : ''}`}>
      <div className="tb-seg" role="tablist" aria-label="Kind">
        {KIND_ORDER.map(k => (
          <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'on' : undefined} onClick={() => setKind(k)}>
            {KIND[k].label}
          </button>
        ))}
      </div>
      <div className="dm-actform-row">
        <input type="date" value={date} max={localISO()} onChange={e => setDate(e.target.value)} style={{ width: 140 }} />
        {brokers.length > 0 && (
          <select value={brokerId} onChange={e => setBrokerId(e.target.value)} style={{ width: 170 }}>
            <option value="">With the party</option>
            {brokers.map(b => <option key={b.id} value={b.id}>With {b.companyName}</option>)}
          </select>
        )}
      </div>
      <textarea rows={compact ? 2 : 3} value={text} onChange={e => setText(e.target.value)} placeholder="What was said, what's next…" />
      {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={add} loading={saving}>Add</Button>
      </div>
    </div>
  );
}

function ActivityList({ items, brokers }: { items: DemandActivity[]; brokers: Entity[] }) {
  const db = getClientDb();
  if (items.length === 0) return <div className="dm-log-empty">No activity yet.</div>;
  return (
    <div className="dm-log-list">
      {items.map(a => (
        <div key={a.id} className="dm-log-item">
          <div className="dm-log-head">
            <KindPill kind={a.kind} />
            <span>{fmtDate(a.date)}</span>
            {a.brokerId && <span>· with {brokers.find(b => b.id === a.brokerId)?.companyName ?? 'broker'}</span>}
            {a.createdByName && <span>· {a.createdByName}</span>}
            <span style={{ marginLeft: 'auto' }}>
              <DeleteIconButton onClick={() => { if (db && window.confirm('Delete this activity line?')) deleteDoc(doc(db, 'demandActivities', a.id)).catch(() => {}); }} />
            </span>
          </div>
          <div className="dm-log-text">{a.text}</div>
        </div>
      ))}
    </div>
  );
}

/** Log a contact with a party that has gone quiet (not tied to one demand). */
function ContactModal({ party, brokers, activities, onClose }: {
  party: Entity; brokers: Entity[]; activities: DemandActivity[]; onClose: () => void;
}) {
  const items = useMemo(() => activities.filter(a => a.partyId === party.id)
    .sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt)), [activities, party.id]);
  return (
    <Modal open onClose={onClose} size="lg" hideHead footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <ModalBanner title={party.companyName} eyebrow="Log a contact" onClose={onClose} />
      <ActivityForm partyId={party.id} brokers={brokers} />
      <div className="form-section-label" style={{ marginTop: 14 }}>All contacts with this party</div>
      <ActivityList items={items} brokers={brokers} />
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

interface QuietRow {
  party: Entity;
  lastSale?: string;
  lastDemand?: string;
  lastContact?: string;
  lastAny?: string;
  days: number | null;
  sold12m: number;
  brokerIds: string[];
  openDemands: number;
}

// Demands tab, grouped two ways (header switch):
// broker = date band, then one band per set of brokers, then the demands;
// party  = one band per party (latest first), its demands newest first.
type GroupBy = 'broker' | 'party';
const DEMAND_COLS = 10;
const PARTY_COLS = 11;
function groupByParty(rows: Demand[]) {
  const out: { partyId: string; latest: string; brokerIds: Set<string>; rows: Demand[] }[] = [];
  for (const d of rows) {
    let g = out[out.length - 1];
    if (!g || g.partyId !== d.partyId) { g = { partyId: d.partyId, latest: d.date, brokerIds: new Set(), rows: [] }; out.push(g); }
    g.rows.push(d);
    if (d.date > g.latest) g.latest = d.date;
    d.brokerIds.forEach(id => g.brokerIds.add(id));
  }
  return out;
}
function groupDemands(rows: Demand[]) {
  const out: { date: string; count: number; brokers: { key: string; ids: string[]; rows: Demand[] }[] }[] = [];
  for (const d of rows) {
    let g = out[out.length - 1];
    if (!g || g.date !== d.date) { g = { date: d.date, count: 0, brokers: [] }; out.push(g); }
    g.count++;
    const key = [...d.brokerIds].sort().join('|');
    let b = g.brokers.find(x => x.key === key);
    if (!b) { b = { key, ids: d.brokerIds, rows: [] }; g.brokers.push(b); }
    b.rows.push(d);
  }
  return out;
}
const fmtDay = (s: string) => {
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};
function FoldMark({ open }: { open: boolean }) {
  return (
    <span className={`dm-fold${open ? ' open' : ''}`} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3.5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </span>
  );
}
function IconBrokerSm() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="6" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 13.5c.6-2.3 2.4-3.5 4.5-3.5s3.9 1.2 4.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M11 3.5a2.3 2.3 0 010 4.4M12.5 10.3c1 .5 1.7 1.6 2 3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export default function DemandList() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);

  const [entities, setEntities] = useState<Entity[]>([]);
  const [demands, setDemands] = useState<Demand[]>([]);
  const [activities, setActivities] = useState<DemandActivity[]>([]);
  const [sales, setSales] = useState<SaleInvoice[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState<'demands' | 'quiet'>('demands');
  const [search, setSearch] = useState('');
  // Broker bands the user flipped (key = date + brokers). Today's and
  // yesterday's start open, older ones start closed.
  const [folded, setFolded] = useState<Set<string>>(() => new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try { return localStorage.getItem('demand-group-by') === 'party' ? 'party' : 'broker'; } catch { return 'broker'; }
  });
  const pickGroupBy = (g: GroupBy) => { setGroupBy(g); try { localStorage.setItem('demand-group-by', g); } catch { /* ignore */ } };
  const toggleFold = (k: string) => setFolded(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [quietDays, setQuietDays] = useState(60);
  const [modal, setModal] = useState<{ edit: Demand | null; partyId?: string } | null>(null);
  const [contactFor, setContactFor] = useState<Entity | null>(null);
  const [delDemand, setDelDemand] = useState<Demand | null>(null);
  const [deleting, setDeleting] = useState(false);
  async function deleteDemand(d: Demand) {
    const db = getClientDb();
    if (!db) return;
    setDeleting(true);
    try {
      // The demand and its own activity lines go together.
      const b = writeBatch(db);
      b.delete(doc(db, 'demands', d.id));
      activities.filter(a => a.demandId === d.id).forEach(a => b.delete(doc(db, 'demandActivities', a.id)));
      await b.commit();
      setDelDemand(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the demand.');
    }
    setDeleting(false);
  }

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const fail = () => setError('Could not read the demand list. Check your connection.');
    const u1 = onSnapshot(collection(db, 'entities'), s => setEntities(s.docs.map(d => ({ id: d.id, ...d.data() } as Entity))));
    const u2 = onSnapshot(collection(db, 'demands'),
      s => { setDemands(s.docs.map(d => ({ id: d.id, ...d.data() } as Demand))); setLoading(false); },
      () => { fail(); setLoading(false); });
    const u3 = onSnapshot(collection(db, 'demandActivities'),
      s => setActivities(s.docs.map(d => ({ id: d.id, ...d.data() } as DemandActivity))), fail);
    const u4 = onSnapshot(collection(db, 'sales'), s => setSales(s.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))), () => {});
    return () => { u1(); u2(); u3(); u4(); };
  }, [business]);

  const entityMap = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
  const name = (id?: string) => (id ? entityMap.get(id)?.companyName ?? '—' : '—');
  // One group per set of brokers; no broker sorts last.
  const brokerKey = (ids: string[]) => ids.length ? ids.map(name).sort((a, b) => a.localeCompare(b)).join(', ') : '\uffff';
  const parties = useMemo(() => entities.filter(e => (e.types ?? []).includes('customer') && e.isActive !== false)
    .sort((a, b) => a.companyName.localeCompare(b.companyName)), [entities]);
  const brokers = useMemo(() => entities.filter(e => (e.types ?? []).includes('broker') && e.isActive !== false)
    .sort((a, b) => a.companyName.localeCompare(b.companyName)), [entities]);

  // Latest contact per party (activities and the demands themselves).
  const lastContactByParty = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of activities) m.set(a.partyId, maxDate(m.get(a.partyId), a.date)!);
    return m;
  }, [activities]);

  // ── Demands tab ──
  const today = localISO();
  const yesterday = localISO(new Date(new Date().setDate(new Date().getDate() - 1)));
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Grouped on screen: date (newest first), then the brokers, then the party.
    const rows = [...demands].sort((a, b) => b.date.localeCompare(a.date)
      || brokerKey(a.brokerIds).localeCompare(brokerKey(b.brokerIds))
      || name(a.partyId).localeCompare(name(b.partyId))
      || b.createdAt.localeCompare(a.createdAt));
    if (!q) return rows;
    return rows.filter(d => [name(d.partyId), ...d.brokerIds.map(name), d.shape, d.size, d.color, d.clarity, d.lab, d.notes]
      .filter(Boolean).join(' ').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demands, search, entityMap]);
  const demandFilters = useMemo<FilterColumn<Demand>[]>(() => [
    { key: 'party',  label: 'Party',   get: d => name(d.partyId) },
    { key: 'shape',  label: 'Shape',   get: d => d.shape ?? '' },
    { key: 'lab',    label: 'Lab',     get: d => d.lab ?? '' },
    { key: 'status', label: 'Status',  get: d => STATUS[d.status].label,
      sort: (a, b) => STATUS_ORDER.findIndex(s => STATUS[s].label === a) - STATUS_ORDER.findIndex(s => STATUS[s].label === b) },
    { key: 'follow', label: 'Follow up', get: d => !d.followUpDate ? '' : d.followUpDate < today ? 'Overdue' : d.followUpDate === today ? 'Today' : 'Later',
      sort: (a, b) => ['Overdue', 'Today', 'Later'].indexOf(a) - ['Overdue', 'Today', 'Later'].indexOf(b) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entityMap, today]);
  const { rows: demandRows, filters: dFilters } = useColumnFilters(searched, demandFilters);
  // Party view: parties by their latest demand, each party's demands newest first.
  const orderedDemands = useMemo(() => {
    if (groupBy === 'broker') return demandRows;
    const latest = new Map<string, string>();
    for (const d of demandRows) if (d.date > (latest.get(d.partyId) ?? '')) latest.set(d.partyId, d.date);
    return [...demandRows].sort((a, b) => (latest.get(b.partyId) ?? '').localeCompare(latest.get(a.partyId) ?? '')
      || name(a.partyId).localeCompare(name(b.partyId)) || a.partyId.localeCompare(b.partyId)
      || b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandRows, groupBy, entityMap]);
  const dPg = usePaging(orderedDemands, `${search}|${dFilters.stamp}|${groupBy}`);

  // ── Gone quiet tab ──
  const quietAll = useMemo<QuietRow[]>(() => {
    const yearAgo = localISO(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
    const byParty = new Map<string, { lastSale?: string; sold12m: number; brokers: Set<string> }>();
    const get = (id: string) => {
      let r = byParty.get(id);
      if (!r) { r = { sold12m: 0, brokers: new Set() }; byParty.set(id, r); }
      return r;
    };
    for (const s of sales) {
      if (!s.toEntityId) continue;
      const r = get(s.toEntityId);
      r.lastSale = maxDate(r.lastSale, s.saleDate);
      if ((s.saleDate ?? '') >= yearAgo) r.sold12m += s.totalAmountUSD ?? 0;
      saleBrokerRows(s).forEach(b => r.brokers.add(b.brokerEntityId));
    }
    const lastDemand = new Map<string, string>();
    for (const d of demands) {
      lastDemand.set(d.partyId, maxDate(lastDemand.get(d.partyId), d.date)!);
      d.brokerIds.forEach(b => get(d.partyId).brokers.add(b));
    }
    return parties
      .filter(p => byParty.has(p.id) || lastDemand.has(p.id))
      .map(p => {
        const r = byParty.get(p.id) ?? { sold12m: 0, brokers: new Set<string>() };
        const lastContact = lastContactByParty.get(p.id);
        const lastAny = maxDate(r.lastSale, lastDemand.get(p.id), lastContact);
        return {
          party: p, lastSale: r.lastSale, lastDemand: lastDemand.get(p.id), lastContact, lastAny,
          days: daysSince(lastAny), sold12m: r.sold12m, brokerIds: [...r.brokers],
          openDemands: demands.filter(d => d.partyId === p.id && (d.status === 'open' || d.status === 'offered')).length,
        };
      });
  }, [sales, demands, parties, lastContactByParty]);
  const quietSearched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quietAll
      .filter(r => (r.days ?? 0) >= quietDays)
      .filter(r => !q || [r.party.companyName, ...r.brokerIds.map(name)].join(' ').toLowerCase().includes(q))
      .sort((a, b) => (b.days ?? 0) - (a.days ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quietAll, quietDays, search, entityMap]);
  const quietFilters = useMemo<FilterColumn<QuietRow>[]>(() => [
    { key: 'party',  label: 'Party',   get: r => r.party.companyName },
    { key: 'broker', label: 'Brokers', get: r => r.brokerIds.length ? r.brokerIds.map(name) : '' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entityMap]);
  const { rows: quietRows, filters: qFilters } = useColumnFilters(quietSearched, quietFilters);
  const qPg = usePaging(quietRows, `${search}|${quietDays}|${qFilters.stamp}`);

  const filters = tab === 'demands' ? dFilters : qFilters;
  const fitRef = useRef<HTMLDivElement>(null);
  useFitHeight(fitRef, 56, 260, [tab, loading, demandRows.length > 0, quietRows.length > 0]);

  const openCount = demands.filter(d => d.status === 'open' || d.status === 'offered').length;
  const followDue = demands.filter(d => (d.status === 'open' || d.status === 'offered') && d.followUpDate && d.followUpDate <= today).length;
  const wonCount  = demands.filter(d => d.status === 'won').length;
  const quietCount = quietAll.filter(r => (r.days ?? 0) >= quietDays).length;
  const narrowed = !!search || filters.active.length > 0;

  const brokerCell = (ids: string[]) => ids.length === 0 ? muted : (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {ids.map(id => <span key={id} className="cell-clip" style={{ maxWidth: 170 }} title={name(id)}>{name(id)}</span>)}
    </span>
  );

  const demandRow = (d: Demand, mode: GroupBy) => {
    const due = (d.status === 'open' || d.status === 'offered') && !!d.followUpDate && d.followUpDate <= today;
    const last = maxDate(d.lastContactDate, lastContactByParty.get(d.partyId));
    return (
      <tr key={d.id} onClick={() => setModal({ edit: d })} style={{ cursor: 'pointer' }}>
        {mode === 'party' ? (<>
          <td className="dm-party-cell" style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{fmtDate(d.date)}</td>
          <td>{brokerCell(d.brokerIds)}</td>
        </>) : (
          <td className="dm-party-cell"><span className="cell-clip" style={{ maxWidth: 200 }} title={name(d.partyId)}>{name(d.partyId)}</span></td>
        )}
        <td style={{ whiteSpace: 'nowrap' }}>{stoneLine(d) || muted}</td>
        <td>{d.color || muted}</td>
        <td>{d.clarity || muted}</td>
        <td>{d.lab || muted}</td>
        <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{d.pricePerCt ? money(d.pricePerCt) : muted}</td>
        <td><StatusPill status={d.status} /></td>
        <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap', color: due ? 'var(--danger)' : undefined, fontWeight: due ? 600 : undefined }}>
          {d.followUpDate ? fmtDate(d.followUpDate) : muted}
        </td>
        <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{last ? fmtDate(last) : muted}</td>
        <td className="dm-pin" onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          <button type="button" className="row-edit-btn" title="Edit" aria-label="Edit" onClick={() => setModal({ edit: d })}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10.5 2.5l3 3L6 13H3v-3l7.5-7.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>
          </button>
          <DeleteIconButton onClick={() => setDelDemand(d)} />
        </td>
      </tr>
    );
  };

  return (
    <AppLayout
      title="Demand List"
      subtitle="What brokers' parties are looking for, and who has gone quiet"
      toolbar={<>
        <div className="tb-seg" role="tablist" aria-label="View">
          <button type="button" role="tab" aria-selected={tab === 'demands'} className={tab === 'demands' ? 'on' : undefined}
            onClick={() => setTab('demands')}>Demands ({demands.length})</button>
          <button type="button" role="tab" aria-selected={tab === 'quiet'} className={tab === 'quiet' ? 'on' : undefined}
            onClick={() => setTab('quiet')}>Gone quiet ({quietCount})</button>
        </div>
        <HeaderSearch value={search} onChange={setSearch} placeholder={tab === 'demands' ? 'Party, broker, stone…' : 'Party or broker…'} width={220} />
        {tab === 'demands' && (
          <div className="tb-seg" role="tablist" aria-label="Group by">
            <button type="button" role="tab" aria-selected={groupBy === 'broker'} className={groupBy === 'broker' ? 'on' : undefined}
              onClick={() => pickGroupBy('broker')} title="By date, then by broker">Broker wise</button>
            <button type="button" role="tab" aria-selected={groupBy === 'party'} className={groupBy === 'party' ? 'on' : undefined}
              onClick={() => pickGroupBy('party')} title="All demands of one party together">Party wise</button>
          </div>
        )}
        {tab === 'quiet' && (
          <div className="tb-seg" role="tablist" aria-label="Quiet for">
            {QUIET_OPTIONS.map(d => (
              <button key={d} type="button" role="tab" aria-selected={quietDays === d} className={quietDays === d ? 'on' : undefined}
                onClick={() => setQuietDays(d)} title={`No sale and no contact for ${d} days or more`}>{d}d</button>
            ))}
          </div>
        )}
        {narrowed && <button type="button" className="tb-clear" onClick={() => { setSearch(''); filters.clear(); }}>Clear</button>}
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setModal({ edit: null })}
          style={{ height: 34, boxSizing: 'border-box' }}>Add Demand</Button>
      }
    >
      {error && <div className="msg msg-error" style={{ marginBottom: 10 }}>{error}</div>}
      <StatChips items={[
        { label: 'Open', value: String(openCount), tone: 'primary' },
        { label: 'Follow up due', value: String(followDue), tone: followDue > 0 ? 'danger' : undefined },
        { label: 'Deal Done', value: String(wonCount), tone: 'success' },
        { label: `Quiet ${quietDays}d+`, value: String(quietCount), tone: quietCount > 0 ? 'warning' : undefined },
      ]} end={<ColumnFilterChips filters={filters} inline />} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : tab === 'demands' ? (
        demandRows.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-3)' }}>
            <IconDemandEmpty />
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', margin: '8px 0 6px' }}>{demands.length ? 'Nothing matches these filters' : 'No demands yet'}</div>
            <div style={{ fontSize: 13 }}>{demands.length ? 'Try clearing a filter.' : 'When a broker tells you what a party wants, add it here.'}</div>
          </div>
        ) : (<>
          <div ref={fitRef} className="table-wrap table-fit table-fit-stats">
            <table className="dm-table">
              <thead><tr>
                {groupBy === 'party' ? (<>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 170 }}>Brokers</th>
                </>) : (
                  <th style={{ width: 220 }} className="th-filterable">Party<ColumnFilterButton filters={dFilters} colKey="party" label="Party" /></th>
                )}
                <th style={{ minWidth: 170 }} className="th-filterable">Stone<ColumnFilterButton filters={dFilters} colKey="shape" label="Shape" /></th>
                <th style={{ width: 70 }}>Color</th>
                <th style={{ width: 80 }}>Clarity</th>
                <th style={{ width: 80 }} className="th-filterable">Lab<ColumnFilterButton filters={dFilters} colKey="lab" label="Lab" /></th>
                <th style={{ width: 110, textAlign: 'right' }}>Price/ct</th>
                <th style={{ width: 100 }} className="th-filterable">Status<ColumnFilterButton filters={dFilters} colKey="status" label="Status" /></th>
                <th style={{ width: 110 }} className="th-filterable">Follow up<ColumnFilterButton filters={dFilters} colKey="follow" label="Follow up" align="right" /></th>
                <th style={{ width: 110 }}>Last contact</th>
                <th className="dm-pin" style={{ width: 72 }} />
              </tr></thead>
              <tbody>
                {groupBy === 'party' ? groupByParty(dPg.pageRows).map(g => {
                  const fk = `p|${g.partyId}`; const shut = (g.latest >= yesterday) === folded.has(fk);
                  const nb = g.brokerIds.size;
                  return [
                    <tr key={`p-${g.partyId}`} className="dm-group-broker dm-group-party" onClick={() => toggleFold(fk)} title={shut ? 'Show demands' : 'Hide demands'}>
                      <td colSpan={PARTY_COLS}><span className="dm-group-label">
                        <FoldMark open={!shut} />{name(g.partyId)}
                        <span className="dm-group-count">
                          {g.rows.length} {g.rows.length === 1 ? 'demand' : 'demands'} · {nb === 0 ? 'no broker' : `${nb} ${nb === 1 ? 'broker' : 'brokers'}`} · latest {fmtDate(g.latest)}
                        </span>
                      </span></td>
                    </tr>,
                    ...(shut ? [] : g.rows).map(d => demandRow(d, 'party')),
                  ];
                }) : groupDemands(dPg.pageRows).map(g => [
                  <tr key={`d-${g.date}`} className="dm-group-date">
                    <td colSpan={DEMAND_COLS}><span className="dm-group-label">{fmtDay(g.date)}<span className="dm-group-count">{g.count} {g.count === 1 ? 'demand' : 'demands'}</span></span></td>
                  </tr>,
                  ...g.brokers.flatMap(b => { const fk = `${g.date}|${b.key}`; const shut = (g.date >= yesterday) === folded.has(fk); return [
                    <tr key={`b-${fk}`} className="dm-group-broker" onClick={() => toggleFold(fk)} title={shut ? 'Show demands' : 'Hide demands'}>
                      <td colSpan={DEMAND_COLS}><span className="dm-group-label">
                        <FoldMark open={!shut} />
                        <IconBrokerSm />{b.ids.length ? b.ids.map(name).sort((x, y) => x.localeCompare(y)).join(' + ') : 'No broker'}
                        <span className="dm-group-count">{b.rows.length}</span>
                      </span></td>
                    </tr>,
                    ...(shut ? [] : b.rows).map(d => demandRow(d, 'broker')),
                  ]; }),
                ])}
              </tbody>
            </table>
          </div>
          <Paging page={dPg.page} totalPages={dPg.totalPages} total={dPg.total} onChange={dPg.setPage} noun="demand" />
        </>)
      ) : (
        quietRows.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-3)' }}>
            <IconDemandEmpty />
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', margin: '8px 0 6px' }}>Nobody has gone quiet</div>
            <div style={{ fontSize: 13 }}>Every party had a sale or a contact in the last {quietDays} days.</div>
          </div>
        ) : (<>
          <div ref={fitRef} className="table-wrap table-fit table-fit-stats">
            <table className="dm-table">
              <thead><tr>
                <th style={{ width: 220 }} className="th-filterable">Party<ColumnFilterButton filters={qFilters} colKey="party" label="Party" /></th>
                <th style={{ width: 110 }}>Last sale</th>
                <th style={{ width: 90, textAlign: 'right' }} title="Days since the last sale, demand or contact">Quiet</th>
                <th style={{ width: 130, textAlign: 'right' }}>Sold (12 months)</th>
                <th style={{ width: 190 }} className="th-filterable">Brokers<ColumnFilterButton filters={qFilters} colKey="broker" label="Brokers" /></th>
                <th style={{ width: 110 }}>Last contact</th>
                <th style={{ width: 90, textAlign: 'right' }}>Open demands</th>
                <th style={{ width: 210 }} />
              </tr></thead>
              <tbody>
                {qPg.pageRows.map(r => (
                  <tr key={r.party.id}>
                    <td><span className="cell-clip" style={{ maxWidth: 210 }} title={r.party.companyName}>{r.party.companyName}</span>
                      {(r.party.phone || r.party.ownerPhone) && <span className="dm-sub">{r.party.phone || r.party.ownerPhone}</span>}
                    </td>
                    <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{r.lastSale ? fmtDate(r.lastSale) : muted}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: (r.days ?? 0) >= 90 ? 'var(--danger)' : 'var(--warning)', fontWeight: 600 }}>
                      {r.days != null ? `${r.days}d` : muted}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{r.sold12m > 0 ? money(r.sold12m) : muted}</td>
                    <td>{brokerCell(r.brokerIds)}</td>
                    <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{r.lastContact ? fmtDate(r.lastContact) : muted}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{r.openDemands || muted}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="dm-act-btn" onClick={() => setContactFor(r.party)}>Log contact</button>
                        <button type="button" className="dm-act-btn" onClick={() => setModal({ edit: null, partyId: r.party.id })}>New demand</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paging page={qPg.page} totalPages={qPg.totalPages} total={qPg.total} onChange={qPg.setPage} noun="party" />
        </>)
      )}

      {modal?.edit && (
        <DemandModal editing={modal.edit} parties={parties} brokers={brokers} activities={activities}
          onClose={() => setModal(null)} />
      )}
      {modal && !modal.edit && (
        <AddDemandsModal parties={parties} brokers={brokers} presetPartyId={modal.partyId} onClose={() => setModal(null)} />
      )}
      {delDemand && (
        <ConfirmModal open title="Delete this demand?"
          message={`The demand from ${name(delDemand.partyId)} (${fmtDate(delDemand.date)}) and its activity lines will be removed. This can't be undone.`}
          confirmLabel="Delete" loading={deleting} onConfirm={() => deleteDemand(delDemand)} onClose={() => setDelDemand(null)} />
      )}
      {contactFor && (
        <ContactModal party={contactFor} brokers={brokers.filter(b => quietAll.find(r => r.party.id === contactFor.id)?.brokerIds.includes(b.id))}
          activities={activities} onClose={() => setContactFor(null)} />
      )}
    </AppLayout>
  );
}
